import { computed, effect, inject, untracked } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AuthService } from '@core/auth/auth.service';
import { InsightsAuthService } from '@core/services/insights-auth.service';
import { concatMap, exhaustMap, from, map, Observable, of, pipe, switchMap, takeWhile, tap, timer } from 'rxjs';
import { InsightsApiService } from './insights-api.service';
import {
  InsightsActionPayload,
  InsightsAssessmentContext,
  InsightsQueryOutcome,
  PersistedGenerationOutcome,
  PersistedInsightGeneration,
} from './models/insights.model';

const POLL_INTERVAL_MS = 30_000;
const CONTINUE_POLLING = 'continue';
const STOP_POLLING = 'stop';

type PollDecision = typeof CONTINUE_POLLING | typeof STOP_POLLING;

export type InsightsView =
  'loading' | 'generating' | 'completed' | 'error' | 'serviceDown' | 'empty' | 'unauthorized' | 'accessUnavailable';

/** Views that settled only because Insights access was missing, so they can be reconciled once it arrives. */
const ACCESS_BLOCKED_VIEWS: ReadonlySet<InsightsView> = new Set<InsightsView>(['empty', 'accessUnavailable']);

type InsightsState = {
  view: InsightsView;
  markdown: string;
  context: InsightsAssessmentContext | null;
  syncWarning: string;
  manualGenerationAvailable: boolean;
};

const initialState: InsightsState = {
  view: 'loading',
  markdown: '',
  context: null,
  syncWarning: '',
  manualGenerationAvailable: false,
};

type LoadRequest = {
  context: InsightsAssessmentContext;
  autoGenerate: boolean;
};

type GenerationSource =
  | { state: 'Initiated'; userId: string }
  | {
      state: 'Completed' | 'Failed';
      record: { uniqueId: string; esgInsightsSummaryText: string; timestamp: string; processingTimestamp: string; userId: string };
    };

const toPersistedGeneration = (context: InsightsAssessmentContext, source: GenerationSource): PersistedInsightGeneration => {
  if (source.state === 'Initiated') {
    return {
      assessmentId: Number(context.assessmentId),
      state: source.state,
      stargateRecordId: null,
      summary: null,
      stargateTimestamp: null,
      stargateUserId: source.userId,
      createdAt: '',
      updatedAt: '',
    };
  }

  return {
    assessmentId: Number(context.assessmentId),
    state: source.state,
    stargateRecordId: source.record.uniqueId || null,
    summary: source.state === 'Completed' ? source.record.esgInsightsSummaryText || null : null,
    stargateTimestamp: source.record.timestamp || source.record.processingTimestamp || null,
    stargateUserId: source.record.userId,
    createdAt: '',
    updatedAt: '',
  };
};

export const InsightsStore = signalStore(
  withState(initialState),
  withComputed(store => ({
    hasNoDisplayableResult: computed(() => store.view() === 'empty'),
    requiresAdviceFallback: computed(
      () =>
        store.view() === 'empty' ||
        store.view() === 'error' ||
        store.view() === 'serviceDown' ||
        store.view() === 'unauthorized' ||
        store.view() === 'accessUnavailable'
    ),
  })),
  withMethods(
    (store, insightsApi = inject(InsightsApiService), authService = inject(AuthService), insightsAuth = inject(InsightsAuthService)) => {
      // Per-assessment guard: ensures generation is triggered at most once per load cycle.
      let hasTriggered = false;
      // Set when the persisted read found nothing, so an empty Stargate result proves neither store holds an insight.
      let persistedGenerationMissing = false;

      const buildPayload = (context: InsightsAssessmentContext): InsightsActionPayload => ({
        company: context.company,
        countries: context.countries,
        sector: context.sector,
        project: context.project,
        assessmentId: context.assessmentId,
        userId: authService.user()?.userId ?? '',
      });

      const setView = (view: InsightsView, markdown = ''): void => {
        patchState(store, { view, markdown, manualGenerationAvailable: false });
      };

      const saveObservedTerminal = (generation: PersistedInsightGeneration): Observable<PollDecision> =>
        insightsApi.saveGeneration$(generation).pipe(
          tap(saved => {
            if (!saved) {
              const stateLabel = generation.state === 'Failed' ? 'failed' : 'completed';
              patchState(store, {
                syncWarning: `The ${stateLabel} Insight could not be synchronized. It remains visible for this session.`,
              });
            }
          }),
          map((): PollDecision => STOP_POLLING)
        );

      // Single polling contract. Returns whether polling should continue.
      const handleQueryOutcome = (outcome: InsightsQueryOutcome, context: InsightsAssessmentContext): Observable<PollDecision> => {
        if (outcome.kind === 'unauthorized') {
          setView('unauthorized');
          return of<PollDecision>(STOP_POLLING);
        }
        if (outcome.kind === 'serviceDown') {
          setView('serviceDown');
          return of<PollDecision>(STOP_POLLING);
        }
        if (outcome.records.length === 0) {
          patchState(store, { view: 'empty', markdown: '', manualGenerationAvailable: persistedGenerationMissing });
          return of<PollDecision>(STOP_POLLING);
        }

        const latest = outcome.records[0];
        const status = (latest.status ?? '').toLowerCase();
        if (status === 'completed') {
          setView('completed', latest.esgInsightsSummaryText ?? '');
          return saveObservedTerminal(toPersistedGeneration(context, { state: 'Completed', record: latest }));
        }
        if (status === 'model could not be executed') {
          setView('error');
          return saveObservedTerminal(toPersistedGeneration(context, { state: 'Failed', record: latest }));
        }
        setView('generating');
        return of<PollDecision>(CONTINUE_POLLING);
      };

      const poll = rxMethod<InsightsAssessmentContext>(
        pipe(
          switchMap(context =>
            timer(0, POLL_INTERVAL_MS).pipe(
              exhaustMap(() =>
                insightsApi.getRecommendation$(context.assessmentId).pipe(concatMap(outcome => handleQueryOutcome(outcome, context)))
              ),
              takeWhile(decision => decision === CONTINUE_POLLING)
            )
          )
        )
      );

      const ensureInsightsAccess = (): Observable<boolean> => {
        return from(insightsAuth.ensureAccess()).pipe(map(() => authService.hasInsightsAccess()));
      };

      // Ensures Insights access, then starts polling for the current generation.
      // When access is unavailable, falls back to noAccessView (the persisted
      // state stays authoritative, so an initiated record keeps showing "generating").
      const pollWithAccess = (context: InsightsAssessmentContext, noAccessView: InsightsView): Observable<PollDecision> =>
        ensureInsightsAccess().pipe(
          tap(hasAccess => {
            if (hasAccess) {
              poll(context);
            } else {
              setView(noAccessView);
            }
          }),
          map((): PollDecision => STOP_POLLING)
        );

      // autoGenerate: trigger generation, persist the initiated state, then poll.
      const startGeneration = (context: InsightsAssessmentContext): Observable<PollDecision> => {
        if (!authService.hasInsightsAccess()) {
          setView('empty');
          return of<PollDecision>(STOP_POLLING);
        }
        if (hasTriggered) {
          return of<PollDecision>(STOP_POLLING);
        }
        hasTriggered = true;
        persistedGenerationMissing = false;
        setView('generating');
        const initiated = toPersistedGeneration(context, {
          state: 'Initiated',
          userId: authService.user()?.userId ?? '',
        });
        return insightsApi.triggerAction$(buildPayload(context)).pipe(
          switchMap(result => {
            if (result !== 'success') {
              setView(result === 'unauthorized' ? 'unauthorized' : 'error');
              return of<PollDecision>(STOP_POLLING);
            }
            return insightsApi.saveGeneration$(initiated).pipe(
              tap(saved => {
                if (!saved) {
                  patchState(store, { syncWarning: 'Insight generation started, but its initiated state could not be synchronized.' });
                }
                // The action succeeded, so the record exists: poll immediately.
                poll(context);
              }),
              map((): PollDecision => STOP_POLLING)
            );
          })
        );
      };

      // Non-autoGenerate: read the authoritative persisted state, then reconcile.
      const handlePersisted = (outcome: PersistedGenerationOutcome, context: InsightsAssessmentContext): Observable<PollDecision> => {
        if (outcome.kind === 'error' || outcome.kind === 'assessmentMissing') {
          setView('serviceDown');
          return of<PollDecision>(STOP_POLLING);
        }

        if (outcome.kind === 'found') {
          if (outcome.generation.state === 'Completed') {
            setView('completed', outcome.generation.summary ?? '');
            return of<PollDecision>(STOP_POLLING);
          }
          if (outcome.generation.state === 'Failed') {
            setView('error');
            return of<PollDecision>(STOP_POLLING);
          }
          // Initiated: reconcile by polling Stargate for the terminal result.
          // Without access, the persisted state remains authoritative but cannot be reconciled.
          setView('generating');
          return pollWithAccess(context, 'accessUnavailable');
        }

        // A new assessment without a persisted generation has no Insights result to wait for.
        // Do not block the Advice panel while an optional sign-in is in progress.
        if (!authService.hasInsightsAccess()) {
          setView('empty');
          return of<PollDecision>(STOP_POLLING);
        }

        persistedGenerationMissing = true;
        return pollWithAccess(context, 'empty');
      };

      const loadInsights = rxMethod<LoadRequest>(
        pipe(
          switchMap(request => {
            if (request.autoGenerate) {
              return startGeneration(request.context);
            }
            return insightsApi
              .getPersistedGeneration$(request.context.assessmentId)
              .pipe(concatMap(outcome => handlePersisted(outcome, request.context)));
          })
        )
      );

      const load = (context: InsightsAssessmentContext, autoGenerate = false): void => {
        hasTriggered = false;
        persistedGenerationMissing = false;
        patchState(store, { context, view: 'loading', markdown: '', syncWarning: '', manualGenerationAvailable: false });
        loadInsights({ context, autoGenerate });
      };

      const generateInsights = (): void => {
        const context = store.context();
        if (!context || !store.manualGenerationAvailable()) {
          return;
        }
        patchState(store, { manualGenerationAvailable: false });
        loadInsights({ context, autoGenerate: true });
      };

      // Re-reads the authoritative state once Insights access arrives late (for example after a
      // sign-in popup completes). Read-only reconcile: generation is never re-triggered.
      const retryAccess = (): void => {
        const context = store.context();
        if (context) {
          load(context);
        }
      };

      return { load, generateInsights, retryAccess };
    }
  ),
  withHooks({
    onInit(store, authService = inject(AuthService)) {
      let hadInsightsAccess = authService.hasInsightsAccess();

      effect(() => {
        const hasAccess = authService.hasInsightsAccess();
        const accessGained = hasAccess && !hadInsightsAccess;
        hadInsightsAccess = hasAccess;
        if (!accessGained) {
          return;
        }
        untracked(() => {
          if (ACCESS_BLOCKED_VIEWS.has(store.view())) {
            store.retryAccess();
          }
        });
      });
    },
  })
);
